<script lang="ts" setup>
	import type { FormRecordsContext } from '~/composables/useFormsFields'
	import type { Dayjs } from 'dayjs'

	interface Emits {
		(event: 'change', arg: unknown, option?: unknown): void
		(event: 'update:value', arg: unknown): void
	}
	type Props = Omit<FormRecordsContext, 'key'>
	const props = defineProps<Props>()

	const emits = defineEmits<Emits>()

	const filteredInput = computed(() => {
		const { rules, inputClass, wrapperClass, options, selectCls, formItemName, type, value, ...rest } = props
		return rest
	})
	const modelValue = defineModel<unknown>('value')

	const attr = useAttrs()
	const filtereAttr = computed(() => (keysToExclude: string[]) => {
		const filtered = Object.fromEntries(Object.entries(attr).filter(([key]) => !keysToExclude.includes(key)))
		return filtered
	})

	const labelInfo = computed(() => {
		const label = props.label || ''
		const match = label.match(/^\s*<([a-z0-9-]+)/i)
		if (!match) return { isHtml: false, tagName: '', isLabelTag: false }
		const tagName = String(match?.[1]).toLowerCase()
		return { isHtml: true, tagName, isLabelTag: tagName === 'label' }
	})
	const { disablePreviousDate, disableFutureYrAndDayAfter, isDate18Min, minOf18 } = useCustomDate()

	const combinedDisabledDate = (currentDate: Dayjs) => {
		if (filteredInput.value?.disabledDate && filteredInput.value.disabledDate(currentDate)) {
			return true
		}
		if (props.isDateEighteenPlus && isDate18Min(currentDate)) {
			return true
		}
		if (!props.canSelectDatesBeforeToday && disablePreviousDate(currentDate)) {
			return true
		}
		if (!props.canSelectDatesAfterToday && disableFutureYrAndDayAfter(currentDate)) {
			return true
		}
		return false
	}

	defineOptions({
		inheritAttrs: false
	})

	const castToString = (val: unknown) => val as string | undefined
	const castToValue = (val: unknown) => val as string | number | undefined
	const castToFile = (val: unknown) => val as File | null | undefined
	const castToSelect = (val: unknown) => val as string | number | boolean | object | null | undefined
	const castToName = (val: unknown) => (typeof val === 'string' ? val : undefined)
</script>

<template>
	<div>
		<form-kit-password-field
			v-if="props.type === 'password'"
			:model-value="castToString(modelValue)"
			:label="labelInfo.isHtml ? '' : props.label"
			:name="castToName(props.formItemName || props.form_name)"
			:placeholder="props.placeholder"
			:input-class="props.inputClass"
			:rules="props.rules"
			:show-indicator="props.showIndicator !== false"
			:show-confirm="!!props.showConfirm"
			:confirm-label="props.confirmLabel"
			:confirm-name="props.confirmName"
			:confirm-placeholder="props.confirmPlaceholder"
			:confirm-input-class="props.confirmInputClass"
			:data-testid="props.form_name || props.name"
			@update:model-value="modelValue = $event"
		/>

		<form-kit-phone-field
			v-else-if="props.type === 'phone'"
			v-model="modelValue as string"
			:rules="props.rules"
			:class-name="cn('!my-0', props.wrapperClass)"
			:label="labelInfo.isHtml ? '' : props.label"
			:name="castToName(props.formItemName || props.form_name)"
			:input="{
				...filteredInput,
				...filtereAttr(['options', 'type', 'form_name']),
				class: props.inputClass
			}"
			:data-testid="props.form_name || props.name"
			:loading="props.loading"
			:maxlength="props.rules?.max"
			@change="e => [emits('change', e || null), emits('update:value', e || ''), props.change?.(e)]"
		/>

		<a-form-item
			v-else
			:label="labelInfo.isHtml ? '' : props.label"
			:rules="props.rules"
			:name="props.formItemName || props.form_name"
			:colon="false"
			:class="cn('!my-0', props.wrapperClass)"
		>
			<template #label>
				<slot name="label" v-bind="props">
					<template v-if="labelInfo.isHtml">
						<label v-if="!labelInfo.isLabelTag" v-html="props.label" />
						<component :is="labelInfo.tagName || 'label'" v-else v-html="props.label" />
					</template>
					<template v-else-if="props.label">
						{{ props.label }}
					</template>
				</slot>
			</template>
			<slot :name="props.form_name">
				<ui-select
					v-if="
						props.type === 'select' && (Array.isArray(props.options) || typeof props.options === 'function')
					"
					:model-value="castToSelect(modelValue)"
					:options="
						props.options as unknown as {
							value: string | number | boolean | object | null
							label?: unknown
						}[]
					"
					:disabled="filteredInput.disabled"
					:label-key="filteredInput.labelKey"
					:value-key="filteredInput.valueKey"
					:placeholder="props.placeholder"
					:show-search="props.showSearch"
					:virtual="props.virtual"
					:name="castToName(props.formItemName || props.form_name)"
					:menu-class="props.menuClass"
					:options-class="props.optionsClass"
					:input-class="props.inputClass"
					:placeholder-class="['text-gray-400', props.placeholderClass]"
					:show-arrow="true"
					:allow-clear="props.allowClear"
					:trigger-class="cn('pl-6 text-sm font-medium bg-gray-50 ', props.selectCls, '!capitalize')"
					:data-testid="props.form_name || props.name"
					:loading="props.loading"
					:form-name="props.form_name"
					:watch-value="filteredInput.watchValue"
					:use-client-search="props.useClientSearch"
					@update:model-value="modelValue = $event"
					@change="
						(e, option) => [emits('change', e, option), emits('update:value', e), props.change?.(e, option)]
					"
				/>

				<a-date-picker
					v-if="props.type === 'date'"
					v-bind="{
						...filteredInput,
						...filtereAttr(['options']),
						disabledDate: combinedDisabledDate,
						defaultPickerValue:
							filteredInput?.defaultPickerValue || props.isDateEighteenPlus ? minOf18 : undefined
					}"
					v-model:value="modelValue as unknown as string | Dayjs"
					:class="cn('custom-input w-full !bg-gray-50 !px-5', props.inputClass)"
					:format="(typeof props.format === 'string' ? props.format : 'DD MMM YYYY') as string"
					value-format="YYYY-MM-DD"
					:data-testid="props.form_name || props.name"
					@change="e => [emits('change', e), props.change?.(e)]"
				>
					<template #suffixIcon>
						<tools-icon dir="line" icon="calendar" :height="20" :width="20" stroke="#667085" />
					</template>
				</a-date-picker>

				<a-input
					v-if="props.type === 'text' || props.type === 'email'"
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					:value="castToValue(modelValue)"
					:type="props.type"
					:class="cn('custom-input w-full !bg-gray-50 !px-5', props.inputClass)"
					:data-testid="props.form_name || props.name"
					:loading="props.loading"
					:maxlength="props.rules?.max"
					@update:value="modelValue = $event"
					@change="
						(e: unknown) => {
							const target = (e as { target?: { value?: string } })?.target
							emits('change', target?.value || null)
							props.change?.(target?.value)
						}
					"
				/>
				<a-input
					v-if="props.type === 'currency'"
					v-naira="true"
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					:value="castToValue(modelValue)"
					inputmode="numeric"
					:class="cn('custom-input w-full !bg-gray-50 !px-5', props.inputClass)"
					:loading="props.loading"
					:data-testid="props.form_name || props.name"
					@update:value="modelValue = $event"
					@change="
						(e: unknown) => {
							const target = (e as { target?: { value?: string } })?.target
							emits('change', target?.value)
							props.change?.(target?.value)
						}
					"
				/>
				<a-input
					v-if="props.type === 'number'"
					v-number="true"
					v-format="props.format"
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					:value="castToValue(modelValue)"
					type="text"
					inputmode="numeric"
					:class="cn('custom-input w-full !bg-gray-50 !px-5', props.inputClass)"
					:loading="props.loading"
					:data-testid="props.form_name"
					@update:value="modelValue = $event"
					@change="
						(e: unknown) => {
							const target = (e as { target?: { value?: string } })?.target
							emits('change', target?.value)
							props.change?.(target?.value)
						}
					"
				/>

				<a-textarea
					v-if="props.type === 'textarea'"
					v-bind="{ ...filteredInput, ...filtereAttr(['options']) }"
					:value="castToValue(modelValue)"
					auto-size
					:class="cn('custom-input !min-h-[84px] w-full !bg-gray-50 !px-5 !py-3', props.inputClass)"
					:data-testid="props.form_name || props.name"
					@update:value="modelValue = $event"
					@change="(e: Event) => [emits('change', (e?.target as HTMLInputElement)?.value), props.change?.(e)]"
				/>
				<shared-upload-input
					v-if="props.type === 'upload'"
					:model-value="castToFile(modelValue)"
					accept=".png,.jpg,.jpeg"
					class="mt-2 w-full"
					:name="props.form_name"
					@update:model-value="modelValue = $event"
					@change="e => [emits('change', e), props.change?.(e)]"
				/>
			</slot>
		</a-form-item>

		<slot :name="`${props.form_name}-description`">
			<p
				v-if="props.description"
				:class="cn('text-[12px] leading-[18px] text-gray-500', props.descriptionClass)"
				v-html="props.description"
			/>
		</slot>
	</div>
</template>
